import { useLoading } from '@gorila-shared-ui/components';
import { OnChangeParams } from 'baseui/select';
import { useEffect, useMemo, useState } from 'react';
import { useDebounceCallback, useDebounceValue } from 'usehooks-ts';
import { useUpdateEffect } from '../../../hooks/useCustomEffects';
import { getJobActivities } from '../../../services/jobTypesService';
import { IdName } from '../../../types/app';
import { JobActivityCore } from '../../../types/jobs';
import EnhancedLabeledSelect from '../../ui/EnhancedLabeledSelect';

type Props = {
  jobTypeId: string;
  omitActivityIdOption?: string;
  activity?: IdName;
  activities?: IdName[];
  selectedActivityId?: string;
  selectedActivityIds?: string[];
  onChangeActivityId?: (activityId?: string) => void;
  onChangeActivity?: (activity?: JobActivityCore) => void;
  onChangeActivityIds?: (activityIds?: string[]) => void;
  onChangeActivities?: (activities?: JobActivityCore[]) => void;
  isMulti?: boolean;
  isFilter?: boolean;
  label?: string;
  emptyOptionText?: string;
  showEmptyOption?: boolean;
  error?: React.ReactNode;
  onBlur?: () => void;
  inline?: boolean;
  clearable?: boolean;
  required?: boolean;
  maxDropdownHeight?: string;
  caption?: React.ReactNode;
};
export function JobActivitySelect({
  jobTypeId,
  omitActivityIdOption,
  selectedActivityId,
  onChangeActivityId,
  onChangeActivity,
  selectedActivityIds,
  onChangeActivityIds,
  onChangeActivities,
  activity,
  activities,
  isFilter = true,
  label = 'Actividad:',
  emptyOptionText = 'Seleciona una actividad',
  showEmptyOption = false,
  error,
  onBlur,
  inline,
  clearable = false,
  required = false,
  isMulti = false,
  maxDropdownHeight,
  caption,
}: Readonly<Props>) {
  const [activitiesList, setActivitiesList] = useState<JobActivityCore[]>();
  const { loading, startLoading, stopLoading } = useLoading();
  const [page, setPage] = useState<number>(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useDebounceValue<string | undefined>(undefined, 350);
  const [selectedActivityOption, setSelectedActivityOption] = useState<{ id: string; label: string }>();
  const [selectedActivityOptions, setSelectedActivityOptions] = useState<{ id: string; label: string }[]>();

  useUpdateEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  useEffect(() => {
    if (page === 0) {
      setPage(1);
      return;
    }
    const loadActivities = async () => {
      startLoading();
      const { paginatedResponse, error } = await getJobActivities(jobTypeId, page, debouncedSearch, 'active');
      if (!error && paginatedResponse) {
        if (page > 1 && activitiesList) {
          setActivitiesList([...activitiesList, ...paginatedResponse.items]);
          setHasNextPage(paginatedResponse.hasNext);
        } else {
          setActivitiesList(paginatedResponse.items);
          setHasNextPage(paginatedResponse.hasNext);
        }
      } else {
        setActivitiesList(undefined);
        setHasNextPage(false);
      }
      stopLoading();
    };
    loadActivities();
  }, [page]);

  const activityOptions = useMemo(() => {
    if (!activitiesList) return;
    const coreActivitiesOptions = activitiesList.map((activity) => ({ id: activity._id, label: activity.name }));
    const activitiesOptions = showEmptyOption
      ? [
          {
            id: undefined,
            label: isFilter ? 'Todos' : emptyOptionText,
          },
          ...coreActivitiesOptions,
        ]
      : coreActivitiesOptions;

    if (activity) {
      if (!activitiesOptions.find((c) => c.id === activity._id))
        activitiesOptions.push({
          id: activity._id,
          label: activity.name,
        });
    } else if (activities) {
      activities.forEach((activity) => {
        if (!activitiesOptions.find((d) => d.id === activity._id))
          activitiesOptions.push({
            id: activity._id,
            label: activity.name,
          });
      });
    }
    if (selectedActivityOption) {
      if (!activitiesOptions.find((c) => c.id === selectedActivityOption.id))
        activitiesOptions.push(selectedActivityOption);
    } else if (selectedActivityOptions) {
      selectedActivityOptions.forEach((activity) => {
        if (!activitiesOptions.find((d) => d.id === activity.id) && activity.id) activitiesOptions.push(activity);
      });
    }
    if (omitActivityIdOption) {
      const index = activitiesOptions.findIndex((a) => a.id === omitActivityIdOption);
      if (index !== -1) activitiesOptions.splice(index, 1);
    }
    return activitiesOptions;
  }, [activitiesList, omitActivityIdOption]);

  const onActivityChange = (params: OnChangeParams) => {
    if (params.option?.id) {
      onChangeActivityId && onChangeActivityId(params.option.id as string);
      onChangeActivity &&
        onChangeActivity({
          _id: params.option?.id,
          name: params.option?.label,
        } as JobActivityCore);
    } else {
      onChangeActivityId && onChangeActivityId(undefined);
      onChangeActivity && onChangeActivity(undefined);
    }
    setSelectedActivityOption(params.option as { id: string; label: string });
    setDebouncedSearch(undefined);
  };

  const onActivitiesChange = (params: OnChangeParams) => {
    if (params.option?.id) {
      onChangeActivityIds &&
        onChangeActivityIds(params.value.map((val) => val.id).filter((id) => id !== undefined) as string[]);
      onChangeActivities &&
        onChangeActivities(
          params.value
            .map((val) => ({ _id: val.id, name: val.label }))
            .filter((option) => option._id !== undefined) as JobActivityCore[]
        );
    } else {
      onChangeActivityIds && onChangeActivityIds(undefined);
      onChangeActivities && onChangeActivities(undefined);
    }
    setSelectedActivityOptions(
      params.value.map((val) => val).filter((val) => val.id !== undefined) as { id: string; label: string }[]
    );
    setDebouncedSearch(undefined);
  };

  const fetchData = () => {
    if (hasNextPage) setPage((page ?? 0) + 1);
  };

  const handleInputChange = useDebounceCallback(function (term: string) {
    setDebouncedSearch(term);
  }, 350);

  return (
    <EnhancedLabeledSelect
      label={label}
      options={activityOptions}
      value={
        isMulti
          ? showEmptyOption || selectedActivityIds !== undefined
            ? selectedActivityIds?.map((activityId) => ({ id: activityId }))
            : undefined
          : showEmptyOption || selectedActivityId !== undefined
          ? [{ id: selectedActivityId }]
          : undefined
      }
      onChange={isMulti ? onActivitiesChange : onActivityChange}
      onInputChange={(e) => {
        handleInputChange(e.target.value);
      }}
      fullWidth
      infiniteScroll
      fetchData={fetchData}
      fetchDataLoading={loading}
      hasNext={hasNextPage}
      onBlur={() => {
        setDebouncedSearch(undefined);
        onBlur && onBlur();
      }}
      searchable
      inline={inline}
      error={error}
      required={required}
      clearable={clearable}
      placeholder={emptyOptionText}
      // onAdd={onAddClick}
      isMulti={isMulti}
      maxDropdownHeight={maxDropdownHeight}
      caption={caption}
    />
  );
}
